import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, Clock, Calendar } from "@/icons";
import { cn } from "@/lib/utils";
import type { Article } from "@/types/content";

/** Reusable blog article card with gradient cover and metadata. */
export function ArticleCard({
  article,
  featured = false,
}: {
  article: Article;
  featured?: boolean;
}) {
  return (
    <article
      data-testid={`article-card-${article.slug}`}
      className={cn(
        "group flex flex-col overflow-hidden rounded-[1.25rem] border border-border bg-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.28)]",
        featured && "md:col-span-2 md:flex-row",
      )}
    >
      <Link
        to={`/insights/${article.slug}`}
        className={cn("flex flex-1 flex-col", featured && "md:flex-row")}
      >
        <div
          className={cn(
            "relative h-44 overflow-hidden",
            featured && "md:h-auto md:w-1/2",
          )}
        >
          <div
            className={cn(
              "absolute inset-0 bg-gradient-to-br transition-transform duration-700 group-hover:scale-105",
              article.cover,
            )}
          />
          <div className="absolute inset-0 bg-grid-light opacity-30" />
          <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-secondary backdrop-blur">
            {article.category}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
            {article.title}
          </h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
            {article.excerpt}
          </p>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {article.tags.slice(0, 3).map((t) => (
              <Badge
                key={t}
                variant="secondary"
                className="rounded-full bg-muted text-xs font-medium text-muted-foreground"
              >
                {t}
              </Badge>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {article.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {article.readingTime}
              </span>
            </span>
            <span className="inline-flex items-center gap-1 font-medium text-primary">
              Read <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}
